import React, { useEffect, useState } from "react";
import { workingHours, updateWorkingHours } from "../../../../Api/apiUtils";
import { Button, EmptyState, Loader, Table, TextInput } from "../../../../ui";
import "./workingHours.scss";
import { toast } from "react-toastify";

interface WorkingHour {
  id: number;
  weekDay: string;
  workingHour: string;
  weekDayNumber?: string; // Added to match API response
}

interface TeacherIdProps {
  teacherId: string;
}

const WorkingHours: React.FC<TeacherIdProps> = ({ teacherId }) => {
  // Define all weekdays in order
  const allWeekdays = [
    { day: "MONDAY", number: "2" },
    { day: "TUESDAY", number: "3" },
    { day: "WEDNESDAY", number: "4" },
    { day: "THURSDAY", number: "5" },
    { day: "FRIDAY", number: "6" },
    { day: "SATURDAY", number: "7" },
    { day: "SUNDAY", number: "1" },
  ];

  const [hours, setHours] = useState<WorkingHour[]>([]);
  const [editedHours, setEditedHours] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Fetch Working Hours and merge with all weekdays
  useEffect(() => {
    setLoading(true);
    const fetchWorkingHours = async () => {
      try {
        const apiData = await workingHours(teacherId);
        
        // Create a complete week by merging API data with all weekdays
        const completeWeek = allWeekdays.map(weekday => {
          const apiEntry = apiData.find((item: WorkingHour) => 
            item.weekDay === weekday.day || 
            item.weekDayNumber === weekday.number
          );
          
          return apiEntry || {
            id: -1 * parseInt(weekday.number), // Temporary negative ID for new days
            weekDay: weekday.day,
            workingHour: "0",
            weekDayNumber: weekday.number
          };
        });

        setHours(completeWeek);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching working hours:", error);
        setLoading(false);
      }
    };

    fetchWorkingHours();
  }, [teacherId]);

  // Handle Editing Working Hours
  const handleEditChange = (id: number, newHours: string) => {
    setEditedHours((prev) => ({
      ...prev,
      [id]: newHours,
    }));
  };

  // Handle Save All Edited Working Hours
  const handleSaveAll = async () => {
    // Filter out days that weren't edited and temporary negative IDs
    const updatedData = hours
      .filter(hour => editedHours[hour.id] !== undefined && hour.id > 0)
      .map((hour) => ({
        teacherId: teacherId,
        weekDay: hour.weekDay,
        hours: editedHours[hour.id] || hour.workingHour,
      }));

    // Handle new days (with negative IDs)
    const newDaysData = hours
      .filter(hour => editedHours[hour.id] !== undefined && hour.id < 0)
      .map((hour) => ({
        teacherId: teacherId,
        weekDay: hour.weekDay,
        hours: editedHours[hour.id] || hour.workingHour,
        isNew: true
      }));

    if (updatedData.length === 0 && newDaysData.length === 0) {
      console.log("No changes to save.");
      return;
    }

    setSaving(true);

    try {
      // Combine both updates and new days
      const allUpdates = [...updatedData, ...newDaysData];
      await updateWorkingHours(allUpdates);

      // Update UI with new values
      setHours((prev) =>
        prev.map((hour) => {
          // For new days (negative IDs), we'd typically get new IDs from API
          // Here we'll just update the working hours
          if (editedHours[hour.id] !== undefined) {
            return {
              ...hour,
              workingHour: editedHours[hour.id],
              id: hour.id > 0 ? hour.id : Math.abs(hour.id) // Convert temp IDs to positive
            };
          }
          return hour;
        })
      );
      
      setEditedHours({});
      setIsEditMode(false);
      toast.success("Working hours updated successfully!");
    } catch (error) {
      console.error("Error updating working hours:", error);
      toast.error("Failed to update working hours.");
    } finally {
      setSaving(false);
    }
  };

  const toggleEditMode = () => {
    setIsEditMode((prev) => !prev);
    if (isEditMode) {
      setEditedHours({});
    }
  };

  return (
    <div className="tp-hours">
      <div className="tp-hours__header">
        <h2 className="tp-hours__title">Working Hours</h2>
        <Button variant="secondary" onClick={toggleEditMode}>
          {isEditMode ? "Cancel Edit" : "Edit"}
        </Button>
      </div>

      {loading ? (
        <Loader label="Loading working hours…" />
      ) : hours.length === 0 ? (
        <EmptyState title="No Data Available" description="No working hours were returned for this teacher." />
      ) : (
        <Table caption="Working hours by weekday">
          <thead>
            <tr>
              <th>Weekday</th>
              <th>Working Hours</th>
            </tr>
          </thead>
          <tbody>
            {hours.map(({ id, weekDay, workingHour }) => {
              const dayLabel = weekDay.charAt(0) + weekDay.slice(1).toLowerCase();
              return (
                <tr key={`${id}-${weekDay}`}>
                  <td>{dayLabel}</td>
                  <td>
                    <TextInput
                      size="sm"
                      type="text"
                      aria-label={`Working hours for ${dayLabel}`}
                      value={editedHours[id] !== undefined ? editedHours[id] : workingHour}
                      onChange={(e) => handleEditChange(id, e.target.value)}
                      disabled={!isEditMode}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {isEditMode && (
        <div className="tp-hours__actions">
          <Button
            variant="primary"
            onClick={handleSaveAll}
            disabled={Object.keys(editedHours).length === 0 || saving}
            loading={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default WorkingHours;